import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { ArrowDownOutlined } from '@ant-design/icons';

import { InlineLoading } from '../shared';
import XoeTurnActivity from './XoeTurnActivity';
import XoeMessageBubble from './XoeMessageBubble';
import type { XoeMessage } from '../../types/xoe/XoeTypes';
import type { XoeTurnProgress } from '../../types/xoe/XoeStreamTypes';
import type { XoeThreadProps } from '../../types/xoe/XoeUiTypes';

// Close enough to count as following; far enough that deliberately scrolling up does not.
const FOLLOW_THRESHOLD_PX = 120;

const XoeThread: React.FC<XoeThreadProps> = ({
	messages,
	isLoading,
	patientId,
	conversationUuid,
	isPending,
	pendingText,
	progress,
	onCancel,
	onAutoContinue,
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const bottomRef = useRef<HTMLDivElement>(null);
	const [following, setFollowing] = useState(true);

	// Memoised on isPending: a fresh object per render would reset ElapsedTimer to 0s every frame.
	const fallbackProgress = useMemo<XoeTurnProgress>(
		() => ({ startedAt: Date.now(), entries: [], capped: false }),
		[isPending],
	);

	const isNearBottom = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return true;
		return el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_THRESHOLD_PX;
	}, []);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return undefined;
		const onScroll = () => setFollowing(isNearBottom());
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, [isNearBottom]);

	const jumpToLatest = useCallback(() => {
		setFollowing(true);
		bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	useEffect(() => {
		if (!following) return;
		bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages.length, isPending, pendingText, following]);

	useEffect(() => {
		setFollowing(true);
	}, [conversationUuid]);

	const optimisticMessage = useMemo<XoeMessage | null>(
		() =>
			pendingText
				? {
						id: -1,
						conversation_id: 0,
						role: 'user',
						content: pendingText,
						content_blocks: null,
						tool_calls: null,
						persona: null,
						status: '',
						created_at: '',
					}
				: null,
		[pendingText],
	);

	// A pending turn on a not-yet-fetched conversation still shows its optimistic bubble and activity.
	const showLoading = isLoading && !isPending && !pendingText;

	const visible = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
	let lastUserText: string | undefined;

	return (
		<div className="mg-xoe-thread" ref={scrollRef} data-testid="xoe-thread">
			<div className="mg-xoe-column">
				{showLoading ? (
					<InlineLoading message="Loading conversation…" />
				) : (
					<>
						{visible.map((m) => {
							const precedingUserText = m.role === 'assistant' ? lastUserText : undefined;
							if (m.role === 'user') lastUserText = m.content;
							return (
								<XoeMessageBubble
									key={m.id}
									message={m}
									patientId={patientId}
									conversationUuid={conversationUuid}
									precedingUserText={precedingUserText}
									onAutoContinue={onAutoContinue}
								/>
							);
						})}

						{optimisticMessage && (
							<XoeMessageBubble
								message={optimisticMessage}
								patientId={patientId}
								conversationUuid={conversationUuid}
								onAutoContinue={onAutoContinue}
								optimistic
							/>
						)}

						{isPending && <XoeTurnActivity progress={progress ?? fallbackProgress} onCancel={onCancel} />}
					</>
				)}

				<div ref={bottomRef} />

				{!following && (
					<div className="mg-xoe-jump-wrap">
						<Button
							className="mg-xoe-jump"
							size="small"
							shape="round"
							icon={<ArrowDownOutlined />}
							onClick={jumpToLatest}
							data-testid="xoe-thread-jump-latest"
						>
							Jump to latest
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default XoeThread;
